import { Link } from 'react-router-dom'
import { useSEO } from '@/hooks/useSEO'

const LAST_UPDATED = 'June 2026'

const h2 = 'font-display text-2xl text-archive-cream mt-10 mb-4'
const h3 = 'font-display text-lg text-archive-cream mt-6 mb-2'
const p = 'mb-4 leading-relaxed text-archive-text'
const ul = 'mb-4 list-disc space-y-2 pl-6 text-archive-text marker:text-archive-gold'
const lead = 'text-archive-cream font-medium'
const link = 'text-archive-gold hover:text-archive-amber transition-colors underline underline-offset-2'

export default function PrivacyPage() {
  useSEO({
    title: 'Privacy Policy',
    description: 'Privacy Policy for The Archive Network — how we collect, use, and protect your information.',
    url: 'https://thearchivenetwork.com/privacy',
  })

  return (
    <div className="animate-fade-in py-16 sm:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-archive-gold">Legal</p>
          <h1 className="mt-4 font-display text-4xl text-archive-cream sm:text-5xl">Privacy Policy</h1>
          <p className="mt-4 text-sm text-archive-muted">Last updated: {LAST_UPDATED}</p>
        </div>

        {/* Body */}
        <div className="mx-auto max-w-3xl">
          <h2 className={h2}>1. Introduction</h2>
          <p className={p}>
            Jonkai Ventures OÜ, an Estonian company ("we," "us," or "our"), operates The Archive Network. This Privacy Policy explains how we
            collect, use, disclose, and safeguard your information when you visit thearchivenetwork.com (the "hub") and
            use our services.
          </p>

          <h2 className={h2}>2. Who We Are &amp; Scope</h2>
          <p className={p}>
            The Archive Network is a family of documentary history and mythology archives — including The Civilization,
            Conflict, Treaty, Lineage, Origin, Classified, Exploration, Mythology, Fraud, and Creed Archives — connected
            by a central hub at thearchivenetwork.com. Jonkai Ventures OÜ, registered in Estonia, is the data controller for the network. This
            policy applies to the hub and to the account and sign-in services the hub provides across the network; each
            archive site also links to this policy.
          </p>

          <h2 className={h2}>3. Information We Collect</h2>
          <h3 className={h3}>Information You Provide</h3>
          <ul className={ul}>
            <li><span className={lead}>Account Information:</span> When you create an account or sign in, we collect your email address.</li>
            <li><span className={lead}>Contact Form:</span> When you use our contact form, we collect your name, email address, and the message you send. These submissions are delivered to the network contact inbox.</li>
          </ul>
          <h3 className={h3}>Information Collected Automatically</h3>
          <ul className={ul}>
            <li><span className={lead}>Usage Data:</span> Information about how you interact with our services, including pages visited and features used.</li>
            <li><span className={lead}>Device Information:</span> Information about the device you use to access our services, including browser type and operating system.</li>
            <li><span className={lead}>Cookies:</span> We use a cookie to maintain your signed-in session (see "Accounts &amp; Single Sign-On" and "Cookies and Sessions" below).</li>
          </ul>

          <h2 className={h2}>4. How We Use Your Information</h2>
          <p className={p}>We use the information we collect to:</p>
          <ul className={ul}>
            <li>Provide, maintain, and improve our services</li>
            <li>Create and manage your account and keep you signed in across the network</li>
            <li>Respond to inquiries submitted through our contact form</li>
            <li>Send you important updates about the services</li>
            <li>Monitor and analyze usage patterns to improve the user experience</li>
            <li>Protect against unauthorized access and abuse</li>
          </ul>

          <h2 className={h2}>5. Accounts &amp; Single Sign-On</h2>
          <p className={p}>
            The hub is the single sign-on (SSO) authority for The Archive Network. You sign in once, with your email
            address, via a secure sign-in link issued by the hub; we do not ask for or store a password. After you sign
            in, the hub issues a session cookie that lets you stay signed in across the hub and the archive sites without
            logging in again at each one. Your account is identified by your email address.
          </p>

          <h2 className={h2}>6. Cookies and Sessions</h2>
          <p className={p}>
            We use an essential session cookie to keep you signed in. It is set after you authenticate, is restricted to
            the network's domains, is marked HttpOnly and Secure, and is not used for advertising. We do not use
            third-party advertising or cross-site tracking cookies. You can control or clear cookies through your browser
            settings; doing so will sign you out.
          </p>

          <h2 className={h2}>7. How We Share Information</h2>
          <p className={p}>
            We do not sell your personal information. We share information only with the service providers (processors)
            that operate the network on our behalf, and where required by law:
          </p>
          <ul className={ul}>
            <li><span className={lead}>Cloudflare</span> — hosting, content delivery, edge compute, and database/storage for the hub and archives.</li>
            <li><span className={lead}>ZeptoMail (Zoho)</span> — delivery of transactional email, such as your sign-in links and contact-form messages.</li>
            <li><span className={lead}>Fastmail</span> — hosting of the network inbox that receives contact-form messages and replies.</li>
            <li><span className={lead}>AI providers</span> — we use third-party AI services (such as OpenAI and Anthropic) to power certain content and interactive features. We do not send them your account credentials.</li>
            <li><span className={lead}>Legal &amp; safety</span> — when required by law, or to protect our rights, users, and the security of the services.</li>
            <li><span className={lead}>Business transfers</span> — in connection with a merger, acquisition, or sale of assets.</li>
          </ul>

          <h2 className={h2}>8. Data Security</h2>
          <p className={p}>
            We implement appropriate technical and organizational measures to protect your personal information. However,
            no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee
            absolute security.
          </p>

          <h2 className={h2}>9. Data Retention</h2>
          <p className={p}>
            We retain your personal information for as long as your account is active or as needed to provide you the
            services. Contact-form messages are retained as needed to handle and follow up on your inquiry. We also
            retain and use information as necessary to comply with legal obligations, resolve disputes, and enforce our
            agreements.
          </p>

          <h2 className={h2}>10. Your Rights</h2>
          <p className={p}>Depending on your location (including under the EU/UK GDPR), you may have the right to:</p>
          <ul className={ul}>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Object to or restrict certain processing</li>
            <li>Request portability of your data</li>
            <li>Lodge a complaint with a data protection supervisory authority</li>
          </ul>
          <p className={p}>
            To exercise any of these rights, please reach us through our{' '}
            <Link to="/contact" className={link}>contact page</Link>. You also have the right to lodge a complaint with
            your local supervisory authority; for us, that is the Estonian Data Protection Inspectorate (Andmekaitse
            Inspektsioon).
          </p>

          <h2 className={h2}>11. International Data Transfers</h2>
          <p className={p}>
            Your information may be transferred to and processed in countries other than your own. Where it is, we rely
            on appropriate safeguards to protect your information in accordance with this Privacy Policy.
          </p>

          <h2 className={h2}>12. Children's Privacy</h2>
          <p className={p}>
            Our services are not directed to children under 13 years of age, and we do not knowingly collect personal
            information from children under 13. If you believe a child has provided us personal information, please
            contact us and we will delete it.
          </p>

          <h2 className={h2}>13. Changes to This Policy</h2>
          <p className={p}>
            We may update this Privacy Policy from time to time. We will post the updated policy on this page and revise
            the "Last updated" date above. Your continued use of the services after changes take effect constitutes
            acceptance of the updated policy.
          </p>

          <h2 className={h2}>14. Contact Us</h2>
          <p className={p}>
            If you have any questions about this Privacy Policy or our data practices, please reach us through our{' '}
            <Link to="/contact" className={link}>contact page</Link>.
          </p>

          {/* Footer links */}
          <div className="mt-14 flex justify-center gap-4 border-t border-archive-stone/40 pt-8 text-sm">
            <Link to="/" className={link}>Home</Link>
            <span className="text-archive-muted">|</span>
            <Link to="/contact" className={link}>Contact</Link>
          </div>
        </div>
      </div>
    </div>
  )
}
